import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { LanguageSchema } from '../common/language.js';
import { ImportFormatSchema } from '../import-export/envelopes.js';
import { ContainerListRuleSchema } from '../zones/requests.js';
// Request schemas for clusters — an operational grouping WITHIN a zone, a
// project-owned reference-data vocabulary (CONVENTIONS §Entity categories)
// managed under `reference_data:manage`. The flat sibling of zones (ADR-052):
// a cluster names its parent zone, but the surface is a flat
// list (zone column + filter), not a tree. Project-scoped only
// (`/api/projects/:projectId/clusters/*`); a cluster never moves between projects.
//
// `name` .max(32) matches clusters.name varchar(32); CI-unique per PROJECT
// (uq_clusters_name_ci — project-wide, not per-zone, like zones). Clusters are
// NOT reorderable — there is no `display_order` (dropped at the genesis baseline);
// the list sorts alphabetically or by column. At-most-one default per project
// (uq_clusters_default_per_project): `is_default` may be set at create (clears any
// prior default in the same tx) and otherwise flips via the dedicated set-default
// action. The parent `zone_id` is set at create and fixed thereafter — moving a
// cluster between zones is a future dedicated action (it is itself a scope-backing
// event, ADR-052), never a PATCH field.
// ── GET /api/projects/:projectId/clusters — the list under a named RULE ─────
// The zones' two rules, shared (`zones/requests.ts`): `manage` (the default —
// the admin pages and the scope editor) · `reach` (any member: the clusters
// holding something their scope reaches — the naming rule, ADR-026 §A).
export const ClustersListQuerySchema = z
    .object({
    rule: ContainerListRuleSchema.default('manage'),
})
    .strict();
// ── POST /api/projects/:projectId/clusters ──────────────────────────────────
export const CreateClusterRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(32),
    zone_id: z.number().int().positive(),
    is_default: z.boolean().default(false),
})
    .strict();
// ── PATCH /api/projects/:projectId/clusters/:clusterId ──────────────────────
// `name` + parent `zone_id` are editable here (the form sends both); `is_default`
// flips via set-default, `is_disabled` via disable/reactivate. Changing `zone_id`
// (reparent) is a `reference_data:manage` vocabulary edit — but ADR-052: if the move
// would orphan any cluster-level chain (an assignee backed only via the old parent
// zone) it is PROHIBITED (CLUSTER_REPARENT_BLOCKED) — a vocabulary edit never silently
// drops accountability. The cluster form previews this first (proactive surfacing).
export const UpdateClusterRequestSchema = z
    .object({
    ...stampedBody,
    name: z.string().trim().min(1).max(32),
    zone_id: z.number().int().positive(),
})
    .strict();
// ── POST /api/projects/:projectId/clusters/:clusterId/reparent/preview ──────
// Read-only: would moving this cluster to `zone_id` orphan any cluster-level chain?
// (ADR-052 §5 proactive surfacing — the form blocks the zone change before save.)
export const ReparentClusterPreviewRequestSchema = z
    .object({
    zone_id: z.number().int().positive(),
})
    .strict();
// ── Cluster definitions import row (create_update, ADR-041) ─────────────────
// One row = one cluster, identified by `name` (the match key, CI-unique per project).
// `zone` is the parent zone NAME, resolved to an id at import (reject-on-miss
// `fk_not_found`). Create-or-skip: a new name creates the cluster under its zone; a
// matched name skips (reparenting an existing cluster is a deliberate edit-form action
// with the backing block, ADR-052 — never a bulk-import side-effect). is_default/status
// are read-only (exported, ignored on import) — mirrors zones.
export const ClusterImportRowSchema = z
    .object({
    name: z.string().trim().min(1).max(32),
    zone: z.string().trim().min(1).max(32),
})
    .strict();
// ── GET /api/projects/:projectId/clusters/export — current-view export ──────
// Mirrors the list's URL view-state so the file IS what the user sees (ADR-045).
// `status` + `zone` arrive comma-separated (the list filters); `q` is the
// free-text name search; `lang` drives localization. Gated `reference_data:manage`.
const csvFilter = z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(z.string().max(64)).max(50).optional());
export const ExportClustersQuerySchema = z
    .object({
    format: ImportFormatSchema.default('xlsx'),
    lang: LanguageSchema.default('en'),
    status: csvFilter,
    zone: csvFilter, // filter by parent zone id(s) — the list's zone facet
    q: z.string().trim().max(100).optional(),
})
    .strict();
// (The cluster-assignments import row schema is DYNAMIC — one ref_list column
// per responsibility, the coverage matrix — so it is built per request by
// core/accountability's assignmentMatrix, not declared here; ADR-059.)
// ── GET …/clusters/assignments/export — current chains, self-bounding to the
// clusters the actor can see (dual-audience read). `q` filters by cluster name;
// `zone` bounds the matrix to clusters of the given zone(s) by id — the list's zone
// facet selects WHICH rows appear, so the matrix honors it too (ADR-045; the
// status lens never applies here — a matrix covers live scopes only). ──
export const ExportClusterAssignmentsQuerySchema = z
    .object({
    format: ImportFormatSchema.default('xlsx'),
    lang: LanguageSchema.default('en'),
    zone: csvFilter,
    q: z.string().trim().max(100).optional(),
})
    .strict();
// GET …/clusters/export/combined — both clusters datasets in ONE workbook (XLSX
// only; ADR-059). The service re-asserts each dataset's own export gate.
export const ClustersCombinedDatasetSchema = z.enum(['definitions', 'assignments']);
export const ExportClustersCombinedQuerySchema = ExportClustersQuerySchema.omit({ format: true })
    .extend({
    datasets: z.preprocess((v) => (typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean) : v), z.array(ClustersCombinedDatasetSchema).min(1).max(2)),
})
    .strict();
